import React, { useRef, useState } from "react";
import { IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ViewPage } from "../../../../../../../assets/icons";
import { AppRoutes } from "../../../../../../../common/routesUtils";
import { PageType } from "../../../../../../../config/page.types";
import { getNodeTypeIcon } from "../../../../../../../constants/nodeTypeIcons";
import { useBreadcrumbs } from "../../../../../../../context/BreadcrumbsProvider";
import { useCurrentTheme } from "../../../../../../../theme/theme";
import { GreenrouterColumn, GreenrouterRow } from "../../../data";
import ColumnMenu from "./ColumnMenu";
import ManageColumnsPopper from "./ManageColumnsPopper";
import SortableHeaderCell from "./SortableHeaderCell";

type Props = {
	isHeader?: boolean;
	columns: GreenrouterColumn[];
	row?: GreenrouterRow;
	sortModel?: {
		field?: keyof GreenrouterRow;
		direction?: "asc" | "desc";
	};
	onSortChange?: (field: keyof GreenrouterRow, direction?: "asc" | "desc") => void;
	onColumnsChange?: (cols: GreenrouterColumn[]) => void;
};

const GridTable = ({ isHeader = false, columns, row, sortModel, onSortChange, onColumnsChange }: Props) => {
	const { t } = useTranslation("feature.units");
	const navigate = useNavigate();
	const { theme } = useCurrentTheme();
	const { visitPage } = useBreadcrumbs();

	const handleRedirect = (event: React.MouseEvent, row: GreenrouterRow) => {
		event.stopPropagation();
		const id = row.id;
		const code = row.code;

		// Update breadcrumb stack
		visitPage(PageType.ResultsOperationalUnitsGreenRouterDetail, false, code, id);

		// Navigate to detail route
		navigate(AppRoutes.ResultOperationalUnitsGreerouterNode.replace(":id", id));
	};

	const columnManageAnchor = useRef<HTMLDivElement | null>(null);
	const [columnsManageOpen, setColumnsManageOpen] = useState(false);
	const [selectedColumn, setSelectedColumn] = useState<GreenrouterColumn | undefined>(undefined);
	const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

	const handleColumnMenuOpen = (col: GreenrouterColumn, e: HTMLElement) => {
		setColumnMenuAnchor(e);
		setSelectedColumn(col);
	};
	const handleColumnMenuClose = () => {
		setColumnMenuAnchor(null);
		setSelectedColumn(undefined);
	};

	const visibleCount = columns.filter((c) => c.visible).length;

	const renderCell = (field: keyof GreenrouterRow, value: unknown) => {
		if (field === "typeDescription") {
			const type = value as {
				typeId?: number;
				typeDescription?: string;
			};

			const Icon = getNodeTypeIcon(type.typeId);
			return (
				<Stack direction="row" spacing={1} alignItems="center">
					{Icon && <Icon />}
					<Typography variant="body2" noWrap>
						{type.typeDescription || "-"}
					</Typography>
				</Stack>
			);
		}

		return (
			<Typography variant="body2" noWrap>
				{String(value ?? "-")}
			</Typography>
		);
	};

	return (
		<Stack
			ref={columnManageAnchor}
			direction="row"
			alignItems="center"
			height={56}
			width="max-content"
			minWidth="100%"
			sx={{
				backgroundColor: isHeader ? theme.palette.primary.main : "transparent",
				position: isHeader ? "sticky" : "relative",
				top: 0,
				zIndex: isHeader ? 2 : 1,
				flexShrink: 0,
			}}
		>
			{isHeader && (
				<>
					<Stack flex="0 0 32px" />
					{/* Sort / hide / manage columns menu */}
					<ColumnMenu
						anchorEl={columnMenuAnchor}
						onClose={handleColumnMenuClose}
						column={selectedColumn}
						columns={columns}
						visibleCount={visibleCount}
						onSort={onSortChange}
						onColumnsChange={onColumnsChange}
						setColumnsOpen={setColumnsManageOpen}
					/>
					{/* Manage columns popper */}
					<ManageColumnsPopper
						anchorEl={columnManageAnchor.current}
						open={columnsManageOpen}
						onClose={() => setColumnsManageOpen(false)}
						columns={columns}
						visibleCount={visibleCount}
						onColumnsChange={onColumnsChange}
					/>
				</>
			)}

			{columns
				.filter((c) => c.visible)
				.map((col) => (
					<Stack
						key={col.field}
						width={col.width}
						flex={col.width ? "0 0 auto" : (col.flex ?? 1)}
						minWidth={80}
						px={1}
						overflow="hidden"
						justifyContent="center"
						sx={{
							flexShrink: 0,
							alignItems: col.field === "emissions" || col.field === "area" ? "flex-end" : "flex-start",
						}}
					>
						{isHeader ? (
							<SortableHeaderCell
								manageColumnsOpen={columnsManageOpen}
								setManageColumnsOpen={setColumnsManageOpen}
								column={col}
								columns={columns}
								visibleCount={visibleCount}
								onColumnsChange={onColumnsChange}
								handleMenuOpen={handleColumnMenuOpen}
								handleMenuClose={handleColumnMenuClose}
								sortModel={sortModel}
								onSort={onSortChange}
							/>
						) : (
							<Stack
								width={col.width ? col.width - 16 : 80} // 16px less for padding
								sx={{
									alignItems:
										col.field === "emissions" || col.field === "area" ? "flex-end" : "flex-start",
								}}
							>
								{renderCell(col.field, row?.[col.field])}
							</Stack>
						)}
					</Stack>
				))}

			<Stack flex="0 0 80px" px={1} justifyContent="center" alignItems={"center"}>
				{isHeader ? (
					<Typography
						variant="body2"
						fontWeight={700}
						sx={{
							color: theme.palette.primary.contrastText,
						}}
					>
						{t("greenrouterList/actions")}
					</Typography>
				) : (
					row && (
						<IconButton
							sx={{ width: "40px", height: "40px" }}
							onClick={(e) => {
								e.stopPropagation();
								handleRedirect(e, row);
							}}
						>
							<ViewPage />
						</IconButton>
					)
				)}
			</Stack>
		</Stack>
	);
};

export default GridTable;
